import { Button, KBD, TypographyDetail } from '@seen/ui'
import { type Editor, isTextSelection } from '@tiptap/react'
import { BubbleMenu, type BubbleMenuProps } from '@tiptap/react/menus'
import { type JSX, useRef, useState } from 'react'
import { Card } from '#src/components/core/Card.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconNewTask } from '#src/components/icons/IconNewTask.tsx'
import { RichText, type RichTextProps } from '#src/components/RichText.tsx'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useInMemoryStore } from '#src/hooks/useInMemoryStore.ts'

export interface TaskableRichTextProps extends Omit<
  RichTextProps,
  'editorRef'
> {
  onSelectCreateTask?: () => void
}

export function TaskableRichText({
  onSelectCreateTask,
  ...delegated
}: TaskableRichTextProps): JSX.Element {
  const editorRef = useRef<Editor | null>(null)

  const setIsGloballyShowingTaskableTextButton = useInMemoryStore(
    (state) => state.setIsShowingTaskableTextButton
  )

  const [showing, setShowing] = useState(false)

  useHotkeys(Hotkeys.NewTaskFromText, () => onSelectCreateTask?.(), {
    enabled: showing,
  })

  return (
    <>
      {editorRef.current && (
        <BubbleMenu
          editor={editorRef.current}
          shouldShow={shouldShow}
          options={{
            onShow: () => {
              setShowing(true)
              setIsGloballyShowingTaskableTextButton(true)
            },
            onHide: () => {
              setShowing(false)
              setIsGloballyShowingTaskableTextButton(false)
            },
          }}
        >
          <Card className="p-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    onSelectCreateTask?.()
                    editorRef.current?.commands.blur()
                  }}
                >
                  <IconNewTask />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="flex items-center gap-2 px-2">
                <TypographyDetail className="text-muted-foreground">
                  Create task from selection
                </TypographyDetail>
                <div className="flex gap-1">
                  <KBD>⌘</KBD>
                  <KBD>shift</KBD>
                  <KBD>O</KBD>
                </div>
              </TooltipContent>
            </Tooltip>
          </Card>
        </BubbleMenu>
      )}
      <RichText editorRef={editorRef} {...delegated} />
    </>
  )
}

const shouldShow: NonNullable<BubbleMenuProps['shouldShow']> = ({
  state,
  from,
  to,
}) => {
  // Based on https://github.com/ueberdosis/tiptap/blob/3e59097b34ce8bc8c39e1def67eb31a1d9f9e5c2/packages/extension-bubble-menu/src/bubble-menu-plugin.ts#L80

  const { doc, selection } = state
  const { empty } = selection

  // Sometime check for `empty` is not enough.
  // Doubleclick an empty paragraph returns a node size of 2.
  // So we check also for an empty text size.
  const isEmptyTextBlock =
    !doc.textBetween(from, to).length && isTextSelection(state.selection)

  if (empty || isEmptyTextBlock) {
    return false
  }

  return true
}
